// LinkUptimeCard — Module 6 KPI card for the RunDetailPage. Displays
// % link uptime + longest outage + a compact recharts sparkline
// generated from /api/runs/{id}/link-summary's timeline points.
import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { sim } from "@/api/endpoints";

export default function LinkUptimeCard({ runId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        if (!runId) return () => {};
        sim.runs
            .linkSummary(runId)
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setErr(e?.message || "Failed to load link summary"); });
        return () => { alive = false; };
    }, [runId]);

    if (err) {
        return (
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 text-[11px] font-mono text-ux-text-3"
                data-testid="run-link-uptime-err"
            >
                RF link summary: {err}
            </div>
        );
    }

    if (!data) {
        return (
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 text-[11px] font-mono text-ux-text-3"
                data-testid="run-link-uptime-loading"
            >
                Loading RF link summary…
            </div>
        );
    }

    if (!data.has_link_data) {
        return (
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 text-[11px] font-mono text-ux-text-3"
                data-testid="run-link-uptime-empty"
            >
                No link data (no terrain + obstacle attachment for this run).
            </div>
        );
    }

    const uptimePct = data.uptime_pct ?? 100;
    const sparkData = (data.timeline || []).map((p) => ({
        t: p.t_s,
        // Map 'clear'→1, 'blocked'→0 for the visual. Area chart fills
        // the green areas; a dipping profile highlights outages clearly.
        y: p.status === "blocked" ? 0 : 1,
    }));
    const uptimeClass =
        uptimePct >= 95
            ? "text-ux-success"
            : uptimePct >= 80
            ? "text-ux-warning"
            : "text-ux-danger";

    return (
        <div
            className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-2"
            data-testid="run-link-uptime-card"
        >
            <div className="flex items-center gap-3">
                <Activity className="h-4 w-4 text-ux-text-3" />
                <h4 className="font-mono text-xs uppercase tracking-wider text-ux-text-3 flex-1">
                    RF link uptime
                </h4>
                <span
                    className={`font-mono text-base font-semibold ${uptimeClass}`}
                    data-testid="run-link-uptime-value"
                >
                    {uptimePct.toFixed(1)}%
                </span>
            </div>
            <div className="text-[11px] font-mono text-ux-text-3 flex gap-4 flex-wrap">
                <span>events: {data.events_count || 0}</span>
                <span>longest outage: {(data.longest_outage_s || 0).toFixed(2)} s</span>
                <span>mean outage: {(data.mean_outage_s || 0).toFixed(2)} s</span>
            </div>
            <div className="h-[70px]" data-testid="run-link-uptime-spark">
                {sparkData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                            <XAxis
                                dataKey="t"
                                tick={false}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis domain={[0, 1]} hide />
                            <Tooltip
                                formatter={(v) => (v === 1 ? "clear" : "blocked")}
                                labelFormatter={(t) => `t = ${Number(t).toFixed(2)} s`}
                                wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }}
                            />
                            <Area
                                type="step"
                                dataKey="y"
                                stroke="#0f766e"
                                fill="#0f766e"
                                fillOpacity={0.35}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : null}
            </div>
        </div>
    );
}
